import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Truck,
  Clock,
  ArrowLeft,
  User,
  MapPin,
  ExternalLink,
  Loader,
  Eye,
  FileText,
  Clipboard
} from "lucide-react";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import { LabService } from "../../services/api/LaborderService/lab.service";
import { Driver } from "../../services/api/LaborderService/types";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface ReturnOrderDetailsProps {
  orderId: number;
  onBack: () => void;
  role: "admin" | "clinic";
}

const ReturnOrderDetails: React.FC<ReturnOrderDetailsProps> = ({ orderId, onBack, role }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnDetails, setReturnDetails] = useState<any>(null);

  // Collapsible Patient Info
  const [patientCollapse, setPatientCollapse] = useState(false);

  // Interactive Verification States for requested items
  const [decisions, setDecisions] = useState<Record<string, { status: "approved" | "rejected"; reason?: string }>>({});
  const [rejectionInputs, setRejectionInputs] = useState<Record<string, string>>({});

  // Rider Assignment State
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string>("");
  const [showRiderError, setShowRiderError] = useState(false);

  // Validation details modal
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Image Viewer Modal State
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Return Rejection Form States
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  const clinicId = user?.role === "clinic" ? user?.id : "";

  useEffect(() => {
    fetchReturnDetails();
    if (role === "clinic" && clinicId) {
      fetchAvailableDrivers();
    }
  }, [orderId, clinicId]);

  const fetchReturnDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getReturnDetails(orderId);
      if (response.success && response.data) {
        setReturnDetails(response.data);

        // Initialize decisions based on existing item statuses if already verified
        const initialDecisions: Record<string, { status: "approved" | "rejected"; reason?: string }> = {};
        response.data.products?.forEach((prod: any) => {
          if (prod.returnItemStatus === "approved" || prod.returnItemStatus === "rejected") {
            initialDecisions[prod.productId] = {
              status: prod.returnItemStatus,
              reason: prod.rejectionReason || undefined
            };
          }
        });
        setDecisions(initialDecisions);
      } else {
        toast.error("Failed to load return details");
      }
    } catch (error) {
      console.error("Error loading return details:", error);
      toast.error("An error occurred while fetching return details");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDrivers = async () => {
    if (!clinicId) return;
    try {
      const response = await LabService.getAvailableDrivers(clinicId, "driver");
      if (response.success) {
        setDrivers(response.data);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const handleApproveItem = (productId: string) => {
    setDecisions(prev => ({
      ...prev,
      [productId]: { status: "approved" }
    }));
  };

  const handleRejectItem = (productId: string, reason: string) => {
    if (!reason.trim()) {
      toast.error("Please enter a reason for rejection");
      return;
    }
    setDecisions(prev => ({
      ...prev,
      [productId]: { status: "rejected", reason }
    }));
    setRejectionInputs(prev => ({ ...prev, [productId]: "" }));
  };

  // Dynamic Refund Calculation
  const calculateRefundDetails = () => {
    if (!returnDetails || !returnDetails.products) return { items: [], total: 0 };

    let total = 0;
    const items: any[] = [];

    returnDetails.products.forEach((prod: any) => {
      const decision = decisions[prod.productId];
      const isApproved = decision
        ? decision.status === "approved"
        : prod.returnItemStatus === "approved";

      if (isApproved) {
        const itemTotal = prod.price * (prod.itemsCount || 1);
        total += itemTotal;
        items.push({
          name: prod.name,
          quantity: prod.itemsCount || 1,
          inwardId: prod.inwardId,
          total: itemTotal
        });
      }
    });

    return { items, total };
  };

  const refundSummary = calculateRefundDetails();

  const handleConfirmSelections = async () => {
    if (!returnDetails) return;

    // Validate all items have decisions
    const unverifiedItems = returnDetails.products.filter((p: any) => !decisions[p.productId]);
    if (unverifiedItems.length > 0) {
      toast.error("Please approve or reject all products first");
      return;
    }

    if (!selectedDriver) {
      setShowRiderError(true);
      toast.error("Please select a rider to pick up the return");
      return;
    }

    try {
      setSubmitting(true);

      const payloadProducts = returnDetails.products.map((prod: any) => {
        const dec = decisions[prod.productId];
        return {
          productId: prod.productId,
          returnItemStatus: dec.status,
          rejectionReason: dec.reason || null,
          itemsCount: prod.itemsCount
        };
      });

      const payload = {
        returnId: returnDetails.id,
        orderId: orderId,
        status: "returned_accepted",
        deliveryPersonId: selectedDriver,
        products: payloadProducts
      };

      const response = await AdminOrderManagementService.verifyReturn(payload);
      if (response.success) {
        toast.success("Rider assigned and return accepted successfully!");
        onBack();
      } else {
        toast.error(response.message || "Failed to submit verification");
      }
    } catch (error) {
      console.error("Verification submit error:", error);
      toast.error("Failed to confirm selections");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateInventory = async () => {
    if (!returnDetails) return;

    const approvedProducts = returnDetails.products
      .filter((prod: any) => {
        const dec = decisions[prod.productId];
        return dec ? dec.status === "approved" : prod.returnItemStatus === "approved";
      })
      .map((prod: any) => ({
        productId: prod.productId,
        inwardId: prod.inwardId,
        itemsCount: prod.itemsCount
      }));

    if (approvedProducts.length === 0) {
      toast.error("No approved items to process");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        returnId: returnDetails.id,
        products: approvedProducts
      };

      const response = await AdminOrderManagementService.updateInventoryAndRefund(payload);
      if (response.success) {
        toast.success("Inventory updated and refund initiated successfully!");
        fetchReturnDetails();
      } else {
        toast.error(response.message || "Failed to update inventory");
      }
    } catch (error) {
      console.error("Update inventory error:", error);
      toast.error("Failed to complete refund and inventory update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectReturnSubmit = async () => {
    if (!cancellationReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        returnId: returnDetails.id,
        orderId: orderId,
        status: "returned_rejected",
        cancellationReason: cancellationReason,
        products: returnDetails.products?.map((prod: any) => ({
          productId: prod.productId,
          returnItemStatus: "rejected",
          rejectionReason: cancellationReason,
          itemsCount: prod.itemsCount
        }))
      };

      const response = await AdminOrderManagementService.verifyReturn(payload);
      if (response.success) {
        toast.success("Return request rejected successfully!");
        onBack();
      } else {
        toast.error(response.message || "Failed to reject return");
      }
    } catch (error) {
      console.error("Reject submit error:", error);
      toast.error("Failed to reject return");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-slate-500">
        <Loader className="animate-spin text-emerald-500 mb-3" size={32} />
        <p className="text-sm font-semibold">Loading return details...</p>
      </div>
    );
  }

  if (!returnDetails) {
    return (
      <div className="p-6 text-center text-slate-500 bg-white rounded-xl border border-slate-200">
        <p className="text-sm font-semibold">Return details could not be found.</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-slate-100 rounded-lg text-xs font-semibold hover:bg-slate-200">
          Go Back
        </button>
      </div>
    );
  }

  const { returnStatus } = returnDetails;
  const inventoryUpdated = Boolean(returnDetails.inventoryUpdated ?? returnDetails.isInventoryUpdated);
  const refundInitiated = Boolean(returnDetails.refundInitiated ?? returnDetails.isRefundProcessed);

  // Count counts of approved and rejected items for Rider Card
  const verifiedProducts = returnDetails.products || [];
  const approvedCount = verifiedProducts.filter((p: any) => (decisions[p.productId]?.status === "approved" || p.returnItemStatus === "approved")).length;
  const rejectedCount = verifiedProducts.filter((p: any) => (decisions[p.productId]?.status === "rejected" || p.returnItemStatus === "rejected")).length;

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-50 overflow-hidden font-sans">
      {/* Top Header */}
      <div className="px-6 py-4 bg-white border-b border-slate-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors border-none bg-transparent cursor-pointer"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-slate-800">Return Request</h1>
              <span className="text-xs font-semibold text-slate-400">|</span>
              <span className="text-sm font-extrabold text-emerald-600">Return #{returnDetails.id}</span>
            </div>
            <p className="text-[0.65rem] text-slate-400 mt-0.5">Original Order: #{returnDetails.orderNumber}</p>
          </div>
        </div>

        <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold tracking-wide uppercase ${returnStatus === "returned_requested" ? "bg-amber-50 text-amber-600 border border-amber-200" :
          returnStatus === "returned_completed" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" :
            returnStatus === "returned_rejected" ? "bg-rose-50 text-rose-600 border border-rose-200" :
              "bg-blue-50 text-blue-600 border border-blue-200"
          }`}>
          {returnStatus.replace(/_/g, " ")}
        </span>
      </div>

      {/* Main Grid Scroll Area */}
      <div className="flex-grow overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column (Span 2) */}
          <div className="lg:col-span-2 space-y-6">

            {/* 1. Patient Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div
                onClick={() => setPatientCollapse(!patientCollapse)}
                className="px-5 py-4 border-b border-slate-200 flex items-center justify-between cursor-pointer hover:bg-slate-50/50"
              >
                <div className="flex items-center gap-2.5">
                  <User size={16} className="text-emerald-500" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Patient Info</span>
                </div>
                {patientCollapse ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </div>

              {!patientCollapse && (
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6 text-[0.8rem]">
                  <div>
                    <span className="text-slate-400 text-[0.7rem] block uppercase">Patient Name</span>
                    <div className="flex items-center gap-2 mt-1">
                      {returnDetails.patientDetails?.patientImageUrl || returnDetails.patientDetails?.orderFor?.imageUrl ? (
                        <img
                          src={returnDetails.patientDetails?.patientImageUrl || returnDetails.patientDetails?.orderFor?.imageUrl}
                          alt="Patient"
                          className="w-7 h-7 rounded-full object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
                          <User size={14} />
                        </div>
                      )}
                      <span className="font-bold text-slate-800">
                        {returnDetails.patientDetails?.patientName || returnDetails.patientDetails?.orderFor?.name || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[0.7rem] block uppercase">Phone Number</span>
                    <span className="font-bold text-slate-800 mt-1 block">
                      {returnDetails.patientDetails?.patientPhone || returnDetails.patientDetails?.orderFor?.mobileNumber || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[0.7rem] block uppercase">Return ID</span>
                    <span className="font-bold text-slate-800 mt-1 block">{returnDetails.id}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[0.7rem] block uppercase">Order ID</span>
                    <span className="font-bold text-slate-800 mt-1 block">{returnDetails.orderNumber}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[0.7rem] block uppercase">Order Date</span>
                    <span className="font-bold text-slate-800 mt-1 block">
                      {returnDetails.returnDate ? new Date(returnDetails.returnDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[0.7rem] block uppercase">Requested on</span>
                    <span className="font-bold text-slate-800 mt-1 block">
                      {returnDetails.createdAt ? new Date(returnDetails.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                  </div>
                  <div className="md:col-span-3 border-t border-slate-100 pt-3">
                    <span className="text-slate-400 text-[0.7rem] block uppercase flex items-center gap-1">
                      <MapPin size={10} /> Address
                    </span>
                    <span className="font-medium text-slate-700 block mt-0.5">
                      {returnDetails.patientDetails?.patientAddress || returnDetails.addressDetails?.address || returnDetails.returnAddress || "No address provided"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* 2. Rider's Pickup Info Card (if completed/assigned) */}
            {returnStatus !== "returned_requested" && returnDetails.driverDetails && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-emerald-500" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Rider's Pickup info</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{returnDetails.driverDetails.name}</h4>
                    <p className="text-[0.7rem] text-slate-500">{returnDetails.driverDetails.mobileNumber}</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.65rem] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200">
                      <Check size={12} /> {approvedCount} Accepted
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.65rem] font-bold bg-rose-50 text-rose-600 border border-rose-200">
                      <X size={12} /> {rejectedCount} Rejected
                    </span>
                  </div>

                  <button
                    onClick={() => setShowValidationModal(true)}
                    className="px-4 py-2 rounded-lg text-xs font-bold bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
                  >
                    View Validation
                  </button>
                </div>
              </div>
            )}

            {/* 3. Return Items List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Return Items</span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[0.8rem]">
                    <thead>
                      <tr className="bg-slate-50 text-[0.68rem] text-slate-500 uppercase font-semibold">
                        <th className="px-5 py-3">Product Name</th>
                        <th className="px-5 py-3">Qty</th>
                        <th className="px-5 py-3">Amount</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Rider Verified</th>
                        <th className="px-5 py-3">Rider Proof</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {verifiedProducts.map((prod: any) => {
                        const decision = decisions[prod.productId];
                        const isApp = decision?.status === "approved";
                        const isRej = decision?.status === "rejected";
                        const itemStatus = prod.returnItemStatus || "pending";
                        const rejectionReason = decision?.reason || prod.rejectionReason;

                        return (
                          <React.Fragment key={prod.productId}>
                            <tr className="hover:bg-slate-50/30">
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {prod.productImage ? (
                                      <img src={prod.productImage} alt={prod.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <FileText size={16} className="text-slate-400" />
                                    )}
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-slate-800 text-xs">{prod.name}</h4>
                                    <span className="text-[0.65rem] text-slate-400 font-mono">Batch: {prod.inwardId || "—"}</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-4 text-xs font-semibold text-slate-700">
                                {prod.itemsCount || 1}
                              </td>
                              <td className="px-5 py-4 font-extrabold text-slate-800 text-xs">
                                ₹{Number(prod.price * (prod.itemsCount || 1)).toFixed(2)}
                              </td>

                              {/* Status (Approved / Rejected) */}
                              <td className="px-5 py-4">
                                {role === "clinic" && returnStatus === "returned_requested" ? (
                                  <div className="flex items-center gap-2">
                                    {isApp ? (
                                      <span className="inline-flex items-center gap-1 text-[0.7rem] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                                        <Check size={12} /> Approved
                                      </span>
                                    ) : isRej ? (
                                      <div className="relative group inline-block">
                                        <span className="inline-flex items-center gap-1 text-[0.7rem] font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-200 cursor-pointer">
                                          <X size={12} /> Rejected
                                        </span>
                                        {rejectionReason && (
                                          <div className="absolute left-0 bottom-full mb-1.5 hidden group-hover:block z-[9999] w-52 p-2.5 text-xs bg-slate-900 text-white rounded-lg shadow-xl pointer-events-none transition-all">
                                            <p className="font-bold text-[0.68rem] text-rose-300">Rejection Reason:</p>
                                            <p className="text-[0.68rem] leading-snug text-slate-200 mt-0.5 whitespace-normal">{rejectionReason}</p>
                                            <div className="absolute -bottom-1 left-4 w-2 h-2 bg-slate-900 rotate-45"></div>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}

                                    {(!isApp && !isRej) ? (
                                      <>
                                        <button
                                          onClick={() => setRejectionInputs(prev => ({ ...prev, [prod.productId]: "" }))}
                                          className="px-3 py-1.5 rounded-lg text-[0.7rem] font-bold bg-white text-rose-600 border border-rose-300 hover:bg-rose-50 hover:border-rose-400 transition-all cursor-pointer"
                                        >
                                          Reject
                                        </button>
                                        <button
                                          onClick={() => handleApproveItem(prod.productId)}
                                          className="px-3 py-1.5 rounded-lg text-[0.7rem] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-500 hover:text-white transition-all cursor-pointer"
                                        >
                                          Approve
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setDecisions(prev => {
                                            const updated = { ...prev };
                                            delete updated[prod.productId];
                                            return updated;
                                          });
                                        }}
                                        className="text-[0.65rem] font-semibold text-slate-400 hover:text-slate-600 hover:underline border-none bg-transparent cursor-pointer ml-1"
                                      >
                                        Change
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  itemStatus === "approved" ? (
                                    <span className="inline-flex items-center gap-1 text-[0.7rem] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                                      <Check size={12} /> Approved
                                    </span>
                                  ) : itemStatus === "rejected" ? (
                                    <div className="relative group inline-block">
                                      <span className="inline-flex items-center gap-1 text-[0.7rem] font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-200 cursor-pointer">
                                        <X size={12} /> Rejected
                                      </span>
                                      {rejectionReason && (
                                        <div className="absolute left-0 bottom-full mb-1.5 hidden group-hover:block z-[9999] w-52 p-2.5 text-xs bg-slate-900 text-white rounded-lg shadow-xl pointer-events-none transition-all">
                                          <p className="font-bold text-[0.68rem] text-rose-300">Rejection Reason:</p>
                                          <p className="text-[0.68rem] leading-snug text-slate-200 mt-0.5 whitespace-normal">{rejectionReason}</p>
                                          <div className="absolute -bottom-1 left-4 w-2 h-2 bg-slate-900 rotate-45"></div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[0.7rem] font-bold text-slate-600 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200 capitalize">
                                      {itemStatus}
                                    </span>
                                  )
                                )}
                              </td>

                              {/* Rider Verified */}
                              <td className="px-5 py-4">
                                <span className={`inline-flex items-center gap-1 text-[0.68rem] font-bold px-2.5 py-1 rounded-lg border ${
                                  prod.isVerified
                                    ? "text-blue-600 bg-blue-50 border-blue-200"
                                    : "text-slate-500 bg-slate-50 border-slate-200"
                                }`}>
                                  {prod.isVerified ? (
                                    <>
                                      <Check size={12} /> Verified
                                    </>
                                  ) : (
                                    "Unverified"
                                  )}
                                </span>
                              </td>

                              {/* Rider Proof Images */}
                              <td className="px-5 py-4">
                                {prod.images && prod.images.length > 0 ? (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {prod.images.map((imgUrl: string, idx: number) => (
                                      <button
                                        key={idx}
                                        onClick={() => setPreviewImage(imgUrl)}
                                        className="w-8 h-8 rounded-md overflow-hidden border border-slate-200 hover:border-emerald-500 transition-colors cursor-pointer bg-slate-50 p-0"
                                        title="Click to view image"
                                      >
                                        <img src={imgUrl} alt={`Proof ${idx + 1}`} className="w-full h-full object-cover" />
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-[0.7rem] text-slate-400 italic">No image</span>
                                )}
                              </td>
                            </tr>

                            {/* Rejection input drawer */}
                            {rejectionInputs[prod.productId] !== undefined && !isRej && (
                              <tr>
                                <td colSpan={6} className="px-5 py-3 bg-slate-50/50">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="text"
                                      placeholder="Reason for rejection (e.g. Seal is broken)..."
                                      className="flex-grow px-3 py-2 rounded-lg border border-slate-300 text-xs bg-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setRejectionInputs(prev => ({ ...prev, [prod.productId]: val }));
                                      }}
                                      value={rejectionInputs[prod.productId] || ""}
                                    />
                                    <button
                                      onClick={() => handleRejectItem(prod.productId, rejectionInputs[prod.productId])}
                                      className="px-4 py-2 rounded-lg text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white border-none cursor-pointer"
                                    >
                                      Confirm Reject
                                    </button>
                                    <button
                                      onClick={() => setRejectionInputs(prev => {
                                        const updated = { ...prev };
                                        delete updated[prod.productId];
                                        return updated;
                                      })}
                                      className="px-3 py-2 rounded-lg text-xs font-bold bg-slate-200 text-slate-700 hover:bg-slate-300 border-none cursor-pointer"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            {/* 4. Reason for Return Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Reason for return</span>

              <div className="space-y-4">
                <p className="text-xs text-slate-700 font-medium bg-slate-50 p-4 rounded-xl leading-relaxed">
                  {returnDetails.returnReason || "No reason specified."}
                </p>

                {returnDetails.returnDocuments && returnDetails.returnDocuments.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[0.68rem] text-slate-400 font-semibold block uppercase">Attachments ({returnDetails.returnDocuments.length})</span>
                    <div className="flex flex-wrap gap-3">
                      {returnDetails.returnDocuments.map((doc: any) => (
                        <button
                          key={doc.id}
                          onClick={() => setPreviewImage(doc.signedUrl || doc.mediaPath)}
                          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 transition-all text-xs font-semibold cursor-pointer max-w-[200px] truncate"
                        >
                          <FileText size={14} className="text-emerald-500" />
                          <span className="truncate">{doc.mediaName || "Attachment"}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Order Note (if present) */}
            {(returnDetails?.ordernote || returnDetails?.orderNote || returnDetails?.order?.ordernote || returnDetails?.order?.orderNote) && (
              <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-xl shadow-sm">
                <div className="flex items-center gap-1.5 mb-1 text-amber-700">
                  <Clipboard size={14} className="flex-shrink-0" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Order Note</span>
                </div>
                <p className="text-xs text-amber-900 leading-relaxed font-medium">
                  {returnDetails?.ordernote || returnDetails?.orderNote || returnDetails?.order?.ordernote || returnDetails?.order?.orderNote}
                </p>
              </div>
            )}

            {/* Refund Details */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Refund Details</span>

              <div className="divide-y divide-slate-100 text-[0.8rem] space-y-3">
                <div className="flex justify-between items-center py-1 pt-0">
                  <span className="text-slate-400">Refund Status</span>
                  <span className="font-extrabold text-[0.68rem] uppercase text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                    {(returnDetails.refundStatus || returnDetails.transaction?.refundStatus || "pending").replace(/_/g, " ")}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">Payment Method</span>
                  <span className="font-bold text-slate-800 uppercase">{returnDetails.transaction?.refundMethod || "UPI"}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">Refund to</span>
                  <span className="font-bold text-slate-800">{returnDetails.transaction?.vpaAddress || returnDetails.transaction?.refundPayoutId || "—"}</span>
                </div>
                <div className="flex justify-between py-2 pb-0">
                  <span className="text-slate-400 font-semibold">Refund amount</span>
                  <span className="font-extrabold text-emerald-600 text-sm">
                    ₹{Number(returnDetails.transaction?.refundAmount ?? returnDetails.returnTotal ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Refund Summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Refund Summary</span>

              <div className="space-y-3">
                {refundSummary.items.length === 0 ? (
                  <p className="text-slate-400 text-xs py-2 italic text-center">No approved items selected</p>
                ) : (
                  <div className="space-y-2">
                    {refundSummary.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-slate-700 font-medium">
                        <span className="truncate max-w-[180px]">{item.name} <span className="text-[0.65rem] text-slate-400">x{item.quantity}</span></span>
                        <span className="font-bold text-slate-800">₹{Number(item.total).toFixed(2)}</span>
                      </div>
                    ))}

                    <div className="pt-3 border-t border-slate-100 flex justify-between text-xs font-bold text-slate-800">
                      <span className="text-slate-500">Total Refund</span>
                      <span className="text-emerald-600 font-extrabold text-[0.85rem]">₹{Number(refundSummary.total).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Card (Only for clinic role) */}
            {role === "clinic" && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Action</span>

                {/* Status indicators for inventoryUpdated & refundInitiated */}
                <div className="p-3 bg-slate-50 rounded-xl space-y-2 border border-slate-100 text-[0.75rem]">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium">Inventory Updated</span>
                    <span className={`px-2 py-0.5 rounded-full text-[0.65rem] font-bold uppercase ${inventoryUpdated ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                      {inventoryUpdated ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium">Refund Initiated</span>
                    <span className={`px-2 py-0.5 rounded-full text-[0.65rem] font-bold uppercase ${refundInitiated ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                      {refundInitiated ? "Yes" : "No"}
                    </span>
                  </div>
                </div>

                {returnStatus === "returned_requested" ? (
                  showRejectForm ? (
                    <div className="space-y-4">
                      <div>
                        <label className="text-[0.68rem] text-slate-400 font-semibold block uppercase mb-1">Reason for Rejection</label>
                        <textarea
                          rows={3}
                          placeholder="Enter the reason why the return is being rejected..."
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs bg-white focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 resize-none"
                          value={cancellationReason}
                          onChange={(e) => setCancellationReason(e.target.value)}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowRejectForm(false);
                            setCancellationReason("");
                          }}
                          className="flex-grow py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 border-none transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleRejectReturnSubmit}
                          disabled={submitting || !cancellationReason.trim()}
                          className="flex-grow py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 border-none disabled:opacity-50 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                        >
                          {submitting ? "Rejecting..." : "Confirm Reject"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="text-[0.68rem] text-slate-400 font-semibold block uppercase mb-1">Assign Rider</label>
                        <select
                          className={`w-full px-3 py-2 rounded-xl border text-xs bg-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 ${showRiderError ? "border-rose-400" : "border-slate-300"
                            }`}
                          value={selectedDriver}
                          onChange={(e) => {
                            setSelectedDriver(e.target.value);
                            setShowRiderError(false);
                          }}
                        >
                          <option value="">-- Choose available rider --</option>
                          {drivers.map(drv => (
                            <option key={drv.id} value={drv.id}>{drv.name} ({drv.mobileNumber})</option>
                          ))}
                        </select>
                        {showRiderError && (
                          <p className="text-[0.65rem] text-rose-500 font-medium mt-1">Please select a driver to continue</p>
                        )}
                      </div>

                      <button
                        onClick={handleConfirmSelections}
                        disabled={submitting}
                        className="w-full py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-md cursor-pointer border-none disabled:opacity-50 transition-all text-center flex items-center justify-center"
                      >
                        {submitting ? (
                          <>
                            <Loader className="animate-spin mr-2" size={14} /> Saving...
                          </>
                        ) : (
                          "Confirm Selections"
                        )}
                      </button>

                      <button
                        onClick={() => setShowRejectForm(true)}
                        className="w-full py-2 rounded-xl text-xs font-bold text-rose-600 border border-rose-300 bg-white hover:bg-rose-50 transition-all text-center cursor-pointer"
                      >
                        Reject Return
                      </button>
                    </div>
                  )
                ) : returnStatus === "returned_rejected" ? (
                  <p className="text-slate-400 text-xs italic text-center py-2">Return request rejected.</p>
                ) : inventoryUpdated && refundInitiated ? (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                    <p className="text-xs font-bold text-emerald-700">No Action Required</p>
                    <p className="text-[0.68rem] text-emerald-600 mt-0.5">Inventory updated & refund completed.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[0.7rem] text-slate-400 leading-relaxed">
                      {!inventoryUpdated && !refundInitiated
                        ? "Updating inventory will add returned stock back to clinic inventory and initiate the customer refund."
                        : !inventoryUpdated
                        ? "Update clinic stock matching return items."
                        : "Initiate customer refund process."}
                    </p>
                    <button
                      onClick={handleUpdateInventory}
                      disabled={submitting}
                      className="w-full py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-md cursor-pointer border-none disabled:opacity-50 transition-all text-center flex items-center justify-center"
                    >
                      {submitting ? (
                        <>
                          <Loader className="animate-spin mr-2" size={14} /> Processing...
                        </>
                      ) : !inventoryUpdated && !refundInitiated ? (
                        `Update Inventory & Proceed Refund (₹${Number(refundSummary.total).toFixed(2)})`
                      ) : !inventoryUpdated ? (
                        "Update Inventory"
                      ) : (
                        `Proceed Refund (₹${Number(refundSummary.total).toFixed(2)})`
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>

        </div>
      </div>

      {/* Validation details Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center p-4" onClick={() => setShowValidationModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[80vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Verification Validation Details</span>
              <button onClick={() => setShowValidationModal(false)} className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-rose-50 hover:text-rose-500 transition-colors border-none cursor-pointer">
                <X size={16} />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-5 space-y-4">
              <div className="divide-y divide-slate-100 text-xs">
                {verifiedProducts.map((prod: any) => {
                  const isApp = decisions[prod.productId]?.status === "approved" || prod.returnItemStatus === "approved";
                  const rejectionReason = decisions[prod.productId]?.reason || prod.rejectionReason;

                  return (
                    <div key={prod.productId} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <h4 className="font-bold text-slate-800">{prod.name}</h4>
                        <span className="text-[0.65rem] text-slate-400">Batch: {prod.inwardId}</span>
                        {rejectionReason && (
                          <p className="text-[0.68rem] text-rose-500 italic mt-0.5">Reason: {rejectionReason}</p>
                        )}
                      </div>

                      <span className={`px-2.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase tracking-wide inline-block ${isApp ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-rose-50 text-rose-600 border border-rose-200"
                        }`}>
                        {isApp ? "Approved" : "Rejected"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[250000] flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-4xl max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center border-none cursor-pointer"
            >
              <X size={20} />
            </button>
            <img src={previewImage} alt="Attachment Preview" className="max-w-full max-h-[80vh] rounded-xl object-contain shadow-2xl border border-white/10" />
          </div>
        </div>
      )}

    </div>
  );
};

export default ReturnOrderDetails;
